import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import { Modal } from "../../Components/Modal";
import ContentItemForm from "../../Components/ContentItemForm";
import TaxonomyMultiSelect from "../../Components/TaxonomyMultiSelect";
import CategoryPathSelect from "../../Components/CategoryPathSelect";
import FieldInfoTip from "../../Components/FieldInfoTip";
import { getFieldUsageNote } from "../../Components/fieldUsageNotes";
import type { ContentTypeConfig } from "../../API/Models";
import {
	CONTENT_CATEGORIES,
	categorizeContentType,
	compareBrowseOrder,
	type ContentCategoryId
} from "./utils";
import { matchesContentTypeQuery } from "./contentTypeSearch";

/* ------------------------------------------------------------------ */
/*  Dynamic-path (CHEMIN) preview                                     */
/* ------------------------------------------------------------------ */

/** Accent-fold → lowercase → hyphenate. Mirrors the engine's NormalizePathSegment
 *  so the preview matches the path the server will actually generate. */
const normalizePathSegment = (value: unknown): string =>
	String(value ?? "")
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "");

/** Bare file name (no folder / query / extension) from a file field value : *  an HrefTarget object ({ external } / { key }) or a plain storage-key string. */
const fileBasenameNoExt = (fileValue: unknown): string => {
	let url = "";
	if (fileValue && typeof fileValue === "object") {
		const obj = fileValue as Record<string, unknown>;
		url = String(obj.external ?? obj.key ?? "");
	} else if (typeof fileValue === "string") {
		url = fileValue;
	}
	if (!url) return "";
	const noQuery = url.split(/[?#]/)[0].replace(/\/+$/, "");
	let segment = noQuery.substring(noQuery.lastIndexOf("/") + 1);
	try {
		segment = decodeURIComponent(segment);
	} catch {
		/* leave raw if not valid percent-encoding */
	}
	const dot = segment.lastIndexOf(".");
	return (dot > 0 ? segment.substring(0, dot) : segment).trim();
};

/**
 * Client-side preview of the read-only CHEMIN, mirroring the engine's
 * BuildDynamicPathAsync (+ the slug/file-name derivation applied on save): so the
 * create modal shows the exact URL the record will get, live as fields are filled.
 * Handles string/number/slug segments; slug segments synthesise from the attached
 * file name → title → date exactly like the server. Returns "" when nothing is
 * resolvable yet (the box then shows the "generated on save" hint).
 */
export const previewDynamicChemin = (config: ContentTypeConfig | null, values: Record<string, any>): string => {
	const segments = config?.dynamicPath?.segments ?? [];
	if (!config || segments.length === 0) return "";

	const getField = (key: string) =>
		Object.entries(values ?? {}).find(([k]) => k.toLowerCase() === key.toLowerCase())?.[1];

	const parts: string[] = [];
	for (const segment of segments) {
		const kind = (segment.kind ?? "").trim().toLowerCase();
		if (kind === "string" || kind === "number") {
			const seg = normalizePathSegment(getField(segment.fieldKey ?? ""));
			if (seg) parts.push(seg);
		} else if (kind === "slug") {
			let slug = normalizePathSegment(getField("slug"));
			if (!slug) {
				// Synthesise like the server: attached file name → title → date.
				let base = "";
				for (const f of config.fields ?? []) {
					if ((f.type ?? "").toLowerCase() !== "file" || !f.key) continue;
					const named = getField(`${f.key}FileName`) || fileBasenameNoExt(getField(f.key));
					base = normalizePathSegment(named);
					if (base) break;
				}
				if (!base) base = normalizePathSegment(getField("title"));
				if (!base) base = normalizePathSegment(getField("date"));
				slug = base;
			}
			if (slug) parts.push(slug);
		}
		// "categories" segments resolve server-side from the picked category path.
	}
	return parts.join("/");
};

/* ------------------------------------------------------------------ */
/*  Content Type Picker Modal                                         */
/* ------------------------------------------------------------------ */

export interface ContentTypePickerModalProps {
	show: boolean;
	onHide: () => void;
	createableTypes: ContentTypeConfig[];
	onSelectType: (type: ContentTypeConfig) => void;
}

type PickerCard = {
	id: string;
	key?: string | null;
	title: string;
	description: string;
	/** Synonymes et sigles du manifeste : cherchables, jamais affiches. */
	keywords?: string;
	isMain: boolean;
	category: ContentCategoryId;
	onPick: () => void;
};

/**
 * Type chooser for "Créer" : the same editorial groups as the browse overview (Actualités,
 * Taux & marchés, …), searchable by de-accented title/description. The hard-coded "Page" card
 * (page editor route) lives in the editorial group; empty groups are hidden.
 */
export const ContentTypePickerModal: React.FC<ContentTypePickerModalProps> = ({
	show,
	onHide,
	createableTypes,
	onSelectType
}) => {
	const history = useHistory();
	const [query, setQuery] = useState("");

	useEffect(() => {
		if (show) setQuery("");
	}, [show]);

	const groups = useMemo(() => {
		const cards: PickerCard[] = [
			{
				id: "__page__",
				title: "Page",
				description: "Créer une nouvelle page du site.",
				keywords: "page rubrique section libre editeur mise en page",
				isMain: true,
				category: "editorial",
				onPick: () => {
					onHide();
					history.push("/new-page");
				}
			}
		];
		createableTypes.forEach((type) => {
			cards.push({
				id: type.id ?? type.title ?? "",
				key: type.key ?? type.id,
				title: type.title ?? "",
				description: type.description?.trim() || "Type de contenu personnalisé.",
				keywords: (type.searchKeywords ?? []).join(" "),
				isMain: Boolean(type.isMain),
				category: categorizeContentType(type.title, type.key ?? type.id),
				onPick: () => onSelectType(type)
			});
		});
		const visible = cards.filter((card) =>
			matchesContentTypeQuery([card.title, card.description, card.key, card.keywords], query)
		);
		return CONTENT_CATEGORIES.map((category) => ({
			...category,
			cards: visible
				.filter((card) => card.category === category.id)
				.sort((a, b) =>
					compareBrowseOrder(
						{ key: a.key, title: a.title, isMain: a.isMain },
						{ key: b.key, title: b.title, isMain: b.isMain }
					)
				)
		})).filter((group) => group.cards.length > 0);
	}, [createableTypes, history, onHide, onSelectType, query]);

	return (
		<Modal show={show} onHide={onHide} size="xl" centered>
			<Modal.Header closeButton>
				<Modal.Title>Créer un contenu</Modal.Title>
			</Modal.Header>
			<Modal.Body className="h-[calc(100vh-14rem)] min-h-[420px] overflow-auto">
				<p className="text-sm text-slate-600 mb-3">Choisissez le type de contenu à créer.</p>
				<div className="relative mb-4">
					<FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
					<input
						autoFocus
						type="text"
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder="Rechercher un type…"
						className="w-full rounded-lg border border-slate-200 py-2 pl-9 pr-3 text-sm focus:border-slate-400 focus:outline-none"
					/>
				</div>
				<div className="space-y-6">
					{groups.map((group) => (
						<section key={`picker-group-${group.id}`} className="space-y-2">
							<div className="border-b border-slate-100 pb-1.5">
								<h4 className="text-xs font-semibold uppercase tracking-wider text-slate-800">{group.label}</h4>
								<p className="text-[11px] text-slate-500">{group.hint}</p>
							</div>
							<div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
								{group.cards.map((card) => (
									<button
										key={`picker-card-${card.id}`}
										type="button"
										onClick={card.onPick}
										className="rounded-xl border border-slate-200 bg-white p-4 text-left transition hover:border-slate-300 hover:shadow-sm"
									>
										<p className="text-sm font-semibold text-slate-900">{card.title}</p>
										<p className="text-xs text-slate-500 mt-1 line-clamp-3">{card.description}</p>
									</button>
								))}
							</div>
						</section>
					))}
					{groups.length === 0 && (
						<p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-500">
							Aucun type ne correspond à votre recherche.
						</p>
					)}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					onClick={onHide}
					className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
				>
					Annuler
				</button>
			</Modal.Footer>
		</Modal>
	);
};

/* ------------------------------------------------------------------ */
/*  Create Content Form Modal                                         */
/* ------------------------------------------------------------------ */

export interface CreateContentFormModalProps {
	show: boolean;
	onHide: () => void;
	createType: ContentTypeConfig | null;
	createValues: Record<string, any>;
	setCreateValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
	createErrors: Record<string, string>;
	createFormError: string | null;
	creating: boolean;
	categories: any[];
	taxonomies: any[];
	onSubmit: () => void;
}

export const CreateContentFormModal: React.FC<CreateContentFormModalProps> = ({
	show,
	onHide,
	createType,
	createValues,
	setCreateValues,
	createErrors,
	createFormError,
	creating,
	categories,
	taxonomies,
	onSubmit
}) => {
	const cheminPreview = useMemo(() => previewDynamicChemin(createType, createValues), [createType, createValues]);
	return (
		<Modal show={show} onHide={onHide} size="xl" centered>
			<Modal.Header closeButton>
				<Modal.Title>{createType?.title ? `Créer : ${createType.title}` : "Créer un contenu"}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{createFormError && (
					<div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700 mb-3">
						{createFormError}
					</div>
				)}
				{createType ? (
					<div className="space-y-4">
						<ContentItemForm
							fields={createType.fields ?? []}
							value={createValues}
							onChange={setCreateValues}
							errors={createErrors}
							disabled={creating}
							contentTypeKey={createType.key ?? createType.id ?? undefined}
							componentsPreviewDefault={false}
						/>
						{createType.supportsDynamicPath && (
							<div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
								<p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
									Chemin
									<FieldInfoTip note={getFieldUsageNote("path")} />
								</p>
								<div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-mono text-slate-700 break-all">
									{cheminPreview || "Le chemin sera généré à l'enregistrement."}
								</div>
							</div>
						)}
						{createType.supportsCategory && (categories ?? []).length > 0 && (
							<div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
								<p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
									Catégories
									<FieldInfoTip note={getFieldUsageNote("categoryPathIds")} />
								</p>
								<CategoryPathSelect
									categories={categories}
									value={(createValues?.categoryPathIds ?? []) as string[]}
									onChange={(next) => setCreateValues((prev) => ({ ...prev, categoryPathIds: next }))}
									placeholder="Choisir un chemin de catégorie"
									helper="Choisissez un seul chemin de catégorie pour ce contenu."
									disabled={creating}
								/>
							</div>
						)}
						{createType.supportsTaxonomy && (
							<div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
								<p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
									Taxonomies
									<FieldInfoTip note={getFieldUsageNote("taxonomyIds")} />
								</p>
								<TaxonomyMultiSelect
									options={taxonomies}
									value={(createValues?.taxonomyIds ?? []).filter(Boolean)}
									onChange={(next) => setCreateValues((prev) => ({ ...prev, taxonomyIds: next }))}
									placeholder="Attribuer des taxonomies"
									helper="Filtrez et sélectionnez les taxonomies de ce contenu."
									disabled={creating}
								/>
							</div>
						)}
					</div>
				) : (
					<p className="text-sm text-slate-600">Sélectionnez un type de contenu pour continuer.</p>
				)}
			</Modal.Body>
			<Modal.Footer>
				<button
					type="button"
					onClick={onHide}
					className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
				>
					Annuler
				</button>
				<button
					type="button"
					onClick={onSubmit}
					disabled={creating || !createType}
					className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
				>
					{creating ? "Création…" : "Créer"}
				</button>
			</Modal.Footer>
		</Modal>
	);
};
